<template>
  <div>
    <!-- 右侧箭头按钮 -->
    <div class="arrow-trigger" :class="{ 'active': isOpen }" @click="togglePanel">
      <i class="el-icon-arrow-left" v-if="!isOpen"></i>
      <i class="el-icon-arrow-right" v-if="isOpen"></i>
    </div>
    
    <!-- 右侧弹出面板 -->
    <transition name="slide-right">
      <div v-show="isOpen" class="detail-panel">
        <div class="panel-header">
          <h3>匹配结果解读</h3>
          <el-button 
            type="text" 
            size="mini" 
            @click="showTestDialog = true"
            style="color: #64748b;"
          >
            <i class="el-icon-edit"></i> 测试接口
          </el-button>
        </div>
        
        <div class="panel-content">
          <!-- 专业详情信息 -->
          <div v-if="majorDetailInfo" class="major-detail-section">
            <div class="major-detail-header">
              <h5>{{ majorDetailInfo.sub_name }}</h5>
              <el-tag 
                v-if="majorDetailInfo.sub_status !== null"
                size="small" 
                :type="majorDetailInfo.sub_status === 1 ? 'success' : 'info'"
                :class="{ 'clickable-tag': majorDetailInfo.sub_status === 1 }"
                @click.native="handleRecruitmentClick"
              >
                {{ majorDetailInfo.sub_status === 1 ? '在招' : '停招' }}
              </el-tag>
            </div>
            
            <!-- 专业说明 -->
            <div v-if="majorDetailInfo.description" class="detail-info-item">
              <h6><i class="el-icon-document"></i> 专业说明</h6>
              <div class="description-sections">
                <div 
                  v-if="parsedDescription.morphology" 
                  class="description-section"
                >
                  <div class="description-label">形态属性</div>
                  <div class="description-content">{{ parsedDescription.morphology }}</div>
                </div>
                <div 
                  v-if="parsedDescription.function" 
                  class="description-section"
                >
                  <div class="description-label">功能属性</div>
                  <div class="description-content">{{ parsedDescription.function }}</div>
                </div>
                <div 
                  v-if="parsedDescription.characteristic" 
                  class="description-section"
                >
                  <div class="description-label">特征属性</div>
                  <div class="description-content">{{ parsedDescription.characteristic }}</div>
                </div>
              </div>
            </div>
            
            <!-- 专业备注 -->
            <div v-if="majorDetailInfo.remarks" class="detail-info-item">
              <h6><i class="el-icon-info"></i> 专业信息</h6>
              <p>{{ majorDetailInfo.remarks }}</p>
            </div>
            
            <!-- 音频播放器 -->
            <div v-if="majorDetailInfo.mp3_url" class="audio-player-section">
              <h6><i class="el-icon-video-play"></i> 专业介绍音频</h6>
              <audio 
                :key="majorDetailInfo.mp3_url"
                :src="majorDetailInfo.mp3_url" 
                controls 
                controlsList="nodownload"
                preload="metadata"
                class="audio-control"
                @error="handleAudioError"
              >
                您的浏览器不支持音频播放
              </audio>
              <div class="audio-info">
                <span class="audio-hint">
                  <i class="el-icon-info"></i> 点击播放按钮收听专业介绍
                </span>
              </div>
            </div>
            
            <!-- 分数详情（可选展开） -->
            <div v-if="majorDetailInfo.score_details" class="detail-info-item collapsible">
              <div class="collapsible-header" @click="toggleScoreDetails">
                <h6><i class="el-icon-data-analysis"></i> 详细评分</h6>
                <i class="el-icon-arrow-down" :class="{ 'expanded': showScoreDetails }"></i>
              </div>
              <transition name="slide-down">
                <div v-show="showScoreDetails" class="score-details-grid">
                  <el-tooltip
                    v-for="(value, key) in parsedScoreDetails" 
                    :key="key"
                    :content="getScoreDescription(key)"
                    placement="right"
                    :disabled="!getScoreDescription(key)"
                    effect="light"
                    popper-class="score-description-tooltip"
                  >
                    <div class="score-item">
                      <span class="score-label">{{ formatScoreLabel(key) }}</span>
                      <el-progress 
                        :percentage="parseInt(value) * 10" 
                        :color="getScoreColor(parseInt(value))"
                        :show-text="false"
                      ></el-progress>
                      <span class="score-value">{{ value }}</span>
                    </div>
                  </el-tooltip>
                </div>
              </transition>
            </div>
          </div>
          
          <!-- 加载状态 -->
          <div v-else-if="loadingMajorDetail" class="loading-placeholder">
            <i class="el-icon-loading"></i> 正在加载专业信息...
          </div>
          
          <!-- 默认提示 -->
          <div v-else class="empty-placeholder">
            <i class="el-icon-info"></i>
            <p>请在左侧选择一个专业查看详细信息</p>
          </div>
        </div>
      </div>
    </transition>
    
    <!-- 遮罩层 -->
    <transition name="fade">
      <div v-show="isOpen" class="panel-overlay" @click="togglePanel"></div>
    </transition>
    
    <!-- 接口测试对话框 -->
    <el-dialog
      title="测试专业信息接口"
      :visible.sync="showTestDialog"
      width="500px"
    >
      <div>
        <el-form label-width="100px">
          <el-form-item label="专业名称">
            <el-input 
              v-model="testMajorName" 
              placeholder="请输入专业名称,如:珠宝首饰设计与工艺"
              clearable
            ></el-input>
          </el-form-item>
          <el-form-item label="国家专业编号">
            <el-input 
              v-model="testMajorCode" 
              placeholder="或输入国家专业编号,如:080701"
              clearable
            ></el-input>
          </el-form-item>
          <el-form-item label="专业ID">
            <el-input 
              v-model="testMajorId" 
              placeholder="或输入专业ID,如:816"
              clearable
              type="number"
            ></el-input>
          </el-form-item>
        </el-form>
        <div style="margin-top: 10px; padding: 10px; background: #f0f9ff; border-radius: 4px; font-size: 12px; color: #0284c7;">
          <i class="el-icon-info"></i> 提示:专业名称、国家专业编号或专业ID至少填写一个。名称示例:"珠宝首饰设计与工艺"，编号示例:"080701"，ID示例:"816"
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showTestDialog = false">取消</el-button>
        <el-button type="primary" @click="testFetchMajorDetail" :loading="loadingMajorDetail">测试查询</el-button>
      </span>
    </el-dialog>
    
    <!-- 大学列表弹窗 -->
    <university-list-dialog
      :visible.sync="showUniversityDialog"
      :sub-id="currentSubId"
      :major-name="majorDetailInfo ? majorDetailInfo.sub_name : ''"
    />
  </div>
</template>

<script>
import request from '@/http/request'
import UniversityListDialog from './UniversityListDialog.vue'

export default {
  name: 'RightDetailPanel',
  components: {
    UniversityListDialog
  },
  props: {
    selectedMajor: {
      type: Number,
      default: null
    },
    displayMajors: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isOpen: false,
      majorDetailInfo: null,
      loadingMajorDetail: false,
      showScoreDetails: false,
      showTestDialog: false,
      testMajorName: '',
      testMajorCode: '',
      testMajorId: '',
      scoreDescriptions: {},
      showUniversityDialog: false,
      currentSubId: null
    }
  },
  computed: {
    parsedDescription() {
      if (!this.majorDetailInfo || !this.majorDetailInfo.description) {
        return {
          morphology: '',
          function: '',
          characteristic: ''
        }
      }
      return this.parseDescription(this.majorDetailInfo.description)
    },
    
    parsedScoreDetails() {
      if (!this.majorDetailInfo || !this.majorDetailInfo.score_details) {
        return {}
      }
      return this.parseScoreDetails(this.majorDetailInfo.score_details)
    }
  },
  watch: {
    selectedMajor: {
      handler(newVal) {
        if (newVal && this.isOpen) {
          this.fetchMajorDetail(newVal)
        }
      },
      immediate: false
    }
  },
  methods: {
    togglePanel() {
      this.isOpen = !this.isOpen
      if (this.isOpen && this.selectedMajor) {
        this.fetchMajorDetail(this.selectedMajor)
      }
    },
    
    getResultFromResponse(response) {
      return response && typeof response === 'object' && 'result' in response 
        ? response.result 
        : response
    },
    
    getEmptyDataErrorMsg(type, value) {
      const msgMap = {
        id: `未找到ID为"${value}"的专业信息，请检查专业ID是否正确`,
        code: `未找到国家专业编号为"${value}"的专业信息，请检查编号是否正确，或该专业可能暂未录入系统`,
        name: `未找到专业名称为"${value}"的专业信息，请检查专业名称是否正确或是否完全匹配，该专业可能暂未录入系统`
      }
      return msgMap[type] || '未找到专业信息'
    },
    
    async fetchMajorDetail(majorId) {
      const major = this.displayMajors.find(m => m.id === majorId)
      if (!major) return
      
      this.loadingMajorDetail = true
      this.majorDetailInfo = null
      this.scoreDescriptions = {}
      
      try {
        const params = major.sub_id ? { sub_id: major.sub_id } : { name: major.name }
        const response = await request('/api/Subspecialty/getSubspecialtyInfo', params, 'post')
        const result = this.getResultFromResponse(response)
        
        if (!result) {
          throw new Error(`未找到专业"${major.name}"的详细信息，该专业可能暂未录入系统或专业信息不匹配`)
        }
        
        this.majorDetailInfo = result
        
        if (result.sub_id && !major.sub_id) {
          major.sub_id = result.sub_id
        }
        
        if (result.score_details) {
          this.parseScoreDetails(result.score_details)
        }
        
        this.$emit('detail-loaded', result)
      } catch (error) {
        console.error('获取专业详情失败:', error)
        
        const errorMessage = error.code === 'ERR_BAD_REQUEST' && error.status === 404
          ? `未找到专业"${major.name}"的详细信息,该专业可能暂未录入系统`
          : `获取专业详情失败: ${error.message || '未知错误'}`
        
        this.$message.warning(errorMessage)
        
        this.majorDetailInfo = {
          sub_name: major.name,
          description: '暂无该专业的详细信息',
          remarks: '该专业可能暂未录入系统,或专业名称不完全匹配。如需了解详情,请联系管理员。',
          sub_status: null,
          mp3_url: null,
          score_details: null
        }
      } finally {
        this.loadingMajorDetail = false
      }
    },
    
    parseScoreDetails(scoreDetailsStr) {
      try {
        let scoreData = {}
        if (typeof scoreDetailsStr === 'string') {
          scoreData = JSON.parse(scoreDetailsStr)
        } else {
          scoreData = scoreDetailsStr
        }
        
        const extractedScores = {}
        const descriptions = {}
        
        if (scoreData.requirement_met) {
          if (scoreData.requirement_met.lemployment_oriented) {
            extractedScores.lemployment_oriented_score = scoreData.requirement_met.lemployment_oriented.score
            descriptions.lemployment_oriented_score = scoreData.requirement_met.lemployment_oriented.description || ''
          }
          if (scoreData.requirement_met.higher_school) {
            extractedScores.higher_school_score = scoreData.requirement_met.higher_school.score
            descriptions.higher_school_score = scoreData.requirement_met.higher_school.description || ''
          }
          if (scoreData.requirement_met.interest_oriented) {
            extractedScores.interest_oriented_score = scoreData.requirement_met.interest_oriented.score
            descriptions.interest_oriented_score = scoreData.requirement_met.interest_oriented.description || ''
          }
        }
        
        if (scoreData.condition_adaptation) {
          if (scoreData.condition_adaptation.score_adaptation) {
            extractedScores.score_adaptation_score = scoreData.condition_adaptation.score_adaptation.score
            descriptions.score_adaptation_score = scoreData.condition_adaptation.score_adaptation.description || ''
          }
          if (scoreData.condition_adaptation.disciplinary_adaptation) {
            extractedScores.disciplinary_adaptation_score = scoreData.condition_adaptation.disciplinary_adaptation.score
            descriptions.disciplinary_adaptation_score = scoreData.condition_adaptation.disciplinary_adaptation.description || ''
          }
          if (scoreData.condition_adaptation.resource_adaptation) {
            extractedScores.resource_adaptation_score = scoreData.condition_adaptation.resource_adaptation.score
            descriptions.resource_adaptation_score = scoreData.condition_adaptation.resource_adaptation.description || ''
          }
        }
        
        if (scoreData.scene_docking) {
          if (scoreData.scene_docking.regional_docking) {
            extractedScores.regional_docking_score = scoreData.scene_docking.regional_docking.score
            descriptions.regional_docking_score = scoreData.scene_docking.regional_docking.description || ''
          }
          if (scoreData.scene_docking.industry_docking) {
            extractedScores.industry_docking_score = scoreData.scene_docking.industry_docking.score
            descriptions.industry_docking_score = scoreData.scene_docking.industry_docking.description || ''
          }
          if (scoreData.scene_docking.policy_oriented) {
            extractedScores.policy_oriented_score = scoreData.scene_docking.policy_oriented.score
            descriptions.policy_oriented_score = scoreData.scene_docking.policy_oriented.description || ''
          }
        }
        
        if (scoreData.data_support) {
          if (scoreData.data_support.employment_support) {
            extractedScores.employment_support_score = scoreData.data_support.employment_support.score
            descriptions.employment_support_score = scoreData.data_support.employment_support.description || ''
          }
          if (scoreData.data_support.higherschool_support) {
            extractedScores.higherschool_support_score = scoreData.data_support.higherschool_support.score
            descriptions.higherschool_support_score = scoreData.data_support.higherschool_support.description || ''
          }
        }
        
        if (scoreData.learning_features) {
          if (scoreData.learning_features.learning_difficulty) {
            extractedScores.learning_difficulty_score = scoreData.learning_features.learning_difficulty.score
            descriptions.learning_difficulty_score = scoreData.learning_features.learning_difficulty.description || ''
          }
          if (scoreData.learning_features.time_investment) {
            extractedScores.time_investment_score = scoreData.learning_features.time_investment.score
            descriptions.time_investment_score = scoreData.learning_features.time_investment.description || ''
          }
          if (scoreData.learning_features.skill_requirements) {
            extractedScores.skill_requirements_score = scoreData.learning_features.skill_requirements.score
            descriptions.skill_requirements_score = scoreData.learning_features.skill_requirements.description || ''
          }
        }
        
        if (scoreData.development_characteristics) {
          if (scoreData.development_characteristics.industry_lifecycle) {
            extractedScores.industry_lifecycle_score = scoreData.development_characteristics.industry_lifecycle.score
            descriptions.industry_lifecycle_score = scoreData.development_characteristics.industry_lifecycle.description || ''
          }
          if (scoreData.development_characteristics.salary_growth) {
            extractedScores.salary_growth_score = scoreData.development_characteristics.salary_growth.score
            descriptions.salary_growth_score = scoreData.development_characteristics.salary_growth.description || ''
          }
          if (scoreData.development_characteristics.job_adaptation) {
            extractedScores.job_adaptation_score = scoreData.development_characteristics.job_adaptation.score
            descriptions.job_adaptation_score = scoreData.development_characteristics.job_adaptation.description || ''
          }
        }
        
        if (scoreData.risk_characteristics) {
          if (scoreData.risk_characteristics.industry_risk) {
            extractedScores.industry_risk_score = scoreData.risk_characteristics.industry_risk.score
            descriptions.industry_risk_score = scoreData.risk_characteristics.industry_risk.description || ''
          }
          if (scoreData.risk_characteristics.difficulty_of_transformation) {
            extractedScores.difficulty_of_transformation_score = scoreData.risk_characteristics.difficulty_of_transformation.score
            descriptions.difficulty_of_transformation_score = scoreData.risk_characteristics.difficulty_of_transformation.description || ''
          }
          if (scoreData.risk_characteristics.alternative_risk) {
            extractedScores.alternative_risk_score = scoreData.risk_characteristics.alternative_risk.score
            descriptions.alternative_risk_score = scoreData.risk_characteristics.alternative_risk.description || ''
          }
        }
        
        if (scoreData.adaptation_features) {
          if (scoreData.adaptation_features.interest_matching) {
            extractedScores.interest_matching_score = scoreData.adaptation_features.interest_matching.score
            descriptions.interest_matching_score = scoreData.adaptation_features.interest_matching.description || ''
          }
          if (scoreData.adaptation_features.lifestyle_adaptation) {
            extractedScores.lifestyle_adaptation_score = scoreData.adaptation_features.lifestyle_adaptation.score
            descriptions.lifestyle_adaptation_score = scoreData.adaptation_features.lifestyle_adaptation.description || ''
          }
        }
        
        this.scoreDescriptions = descriptions
        
        return extractedScores
      } catch (error) {
        console.error('解析分数详情失败:', error)
        return {}
      }
    },
    
    formatScoreLabel(key) {
      const labelMap = {
        lemployment_oriented_score: '就业导向',
        higher_school_score: '升学导向',
        interest_oriented_score: '兴趣导向',
        score_adaptation_score: '分数适配',
        disciplinary_adaptation_score: '学科优势适配',
        resource_adaptation_score: '资源背景适配',
        regional_docking_score: '地域场景对接',
        industry_docking_score: '行业场景对接',
        policy_oriented_score: '政策场景对接',
        employment_support_score: '就业数据支撑',
        higherschool_support_score: '升学数据支撑',
        learning_difficulty_score: '学习难度',
        time_investment_score: '时间投入',
        skill_requirements_score: '技能要求',
        industry_lifecycle_score: '行业生命周期',
        salary_growth_score: '薪资增长',
        job_adaptation_score: '岗位适配性',
        industry_risk_score: '行业风险',
        difficulty_of_transformation_score: '转型难度',
        alternative_risk_score: '替代风险',
        interest_matching_score: '兴趣匹配',
        lifestyle_adaptation_score: '生活方式适配'
      }
      return labelMap[key] || key
    },
    
    getScoreColor(score) {
      if (score >= 8) return '#67c23a'
      if (score >= 6) return '#409eff'
      if (score >= 4) return '#e6a23c'
      return '#f56c6c'
    },
    
    getScoreDescription(key) {
      return this.scoreDescriptions[key] || ''
    },
    
    toggleScoreDetails() {
      this.showScoreDetails = !this.showScoreDetails
    },
    
    parseDescription(description) {
      if (!description) {
        return {
          morphology: '',
          function: '',
          characteristic: ''
        }
      }
      
      const result = {
        morphology: '',
        function: '',
        characteristic: ''
      }
      
      const morphologyMatch = description.match(/形态属性[：:](.*?)(?=功能属性[：:]|$)/s)
      const functionMatch = description.match(/功能属性[：:](.*?)(?=特征属性[：:]|$)/s)
      const characteristicMatch = description.match(/特征属性[：:](.*?)$/s)
      
      if (morphologyMatch) {
        result.morphology = morphologyMatch[1].trim()
      }
      
      if (functionMatch) {
        result.function = functionMatch[1].trim()
      }
      
      if (characteristicMatch) {
        result.characteristic = characteristicMatch[1].trim()
      }
      
      return result
    },
    
    handleAudioError(e) {
      const audio = e.target
      let errorMsg = '音频加载失败，请检查网络连接'
      
      if (audio.error) {
        const errorMessages = {
          1: '音频加载被中止',
          2: '网络错误，无法加载音频',
          3: '音频解码失败，格式可能不支持',
          4: '音频文件不存在或URL无效'
        }
        errorMsg = errorMessages[audio.error.code] || errorMsg
      }
      
      this.$message.warning(errorMsg)
    },
    
    async testFetchMajorDetail() {
      if (!this.testMajorName && !this.testMajorCode && !this.testMajorId) {
        this.$message.warning('请输入专业名称、国家专业编号或专业ID')
        return
      }
      
      this.loadingMajorDetail = true
      this.scoreDescriptions = {}
      
      try {
        let params = {}
        
        if (this.testMajorId) {
          const id = parseInt(this.testMajorId)
          if (isNaN(id) || id <= 0) {
            this.$message.warning('专业ID必须是正整数')
            this.loadingMajorDetail = false
            return
          }
          params.sub_id = id
        } else if (this.testMajorCode) {
          params.code = this.testMajorCode.trim()
        } else if (this.testMajorName) {
          params.name = this.testMajorName.trim()
        }
        
        const response = await request('/api/Subspecialty/getSubspecialtyInfo', params, 'post')
        const result = this.getResultFromResponse(response)
        
        if (!result) {
          const errorType = this.testMajorId ? 'id' : (this.testMajorCode ? 'code' : 'name')
          const errorValue = this.testMajorId || this.testMajorCode || this.testMajorName
          this.$message.warning(this.getEmptyDataErrorMsg(errorType, errorValue))
          this.loadingMajorDetail = false
          return
        }
        
        if (this.testMajorCode && result.remarks) {
          const remarksCode = this.extractCodeFromRemarks(result.remarks)
          const inputCode = this.testMajorCode.trim()
          if (remarksCode && remarksCode !== inputCode) {
            this.$message.warning(`查询成功，但国家专业编号不匹配（实际编号：${remarksCode}）`)
          }
        }
        
        this.majorDetailInfo = result
        
        if (result.score_details) {
          this.parseScoreDetails(result.score_details)
        }
        this.showTestDialog = false
        this.$message.success('查询成功!')
      } catch (error) {
        console.error('测试接口失败:', error)
        
        const errorMessage = error.code === 'ERR_BAD_REQUEST' && error.status === 404
          ? '未找到该专业信息,请检查专业名称、国家专业编号或专业ID是否正确'
          : `接口调用失败: ${error.message || '未知错误'}`
        
        this.$message.error(errorMessage)
      } finally {
        this.loadingMajorDetail = false
      }
    },
    
    extractCodeFromRemarks(remarks) {
      if (!remarks) return ''
      const match = remarks.match(/^(\d+)/)
      return match ? match[1] : ''
    },
    
    handleRecruitmentClick() {
      // 只有当专业状态为"在招"时才显示大学列表
      if (this.majorDetailInfo && this.majorDetailInfo.sub_status === 1) {
        const subId = this.majorDetailInfo.sub_id
        if (!subId) {
          this.$message.warning('专业ID不存在，无法查询大学列表')
          return
        }
        this.currentSubId = subId
        this.showUniversityDialog = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.arrow-trigger {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 80px;
  background: #3b82f6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px 0 0 8px;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s;
  z-index: 1001;
  
  &:hover {
    background: #2563eb;
    width: 45px;
  }
  
  &.active {
    right: 400px;
  }
  
  i {
    font-size: 20px;
    font-weight: bold;
  }
}

.detail-panel {
  position: fixed;
  right: 0;
  top: 0;
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow-y: auto;
}

.panel-header {
  padding: 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
  }
}

.panel-content {
  padding: 20px;
}

.major-detail-section {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.major-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
  
  h5 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
  }
  
  .clickable-tag {
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(103, 194, 58, 0.3);
    }
  }
}

.detail-info-item {
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  h6 {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    
    i {
      margin-right: 6px;
      color: #3b82f6;
    }
  }
  
  p {
    font-size: 12px;
    line-height: 1.6;
    color: #64748b;
    margin: 0;
    padding: 8px 12px;
    background: white;
    border-radius: 4px;
    border-left: 3px solid #3b82f6;
  }
  
  .description-sections {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .description-section {
    background: white;
    border-radius: 4px;
    border-left: 3px solid #3b82f6;
    overflow: hidden;
  }
  
  .description-label {
    font-size: 13px;
    font-weight: 600;
    color: #3b82f6;
    padding: 8px 12px;
    background: #eff6ff;
    border-bottom: 1px solid #dbeafe;
  }
  
  .description-content {
    font-size: 12px;
    line-height: 1.6;
    color: #64748b;
    padding: 10px 12px;
    margin: 0;
  }
  
  &.collapsible {
    background: white;
    border-radius: 6px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background: #f1f5f9;
    }
  }
}

.collapsible-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h6 {
    margin: 0;
  }
  
  .el-icon-arrow-down {
    font-size: 14px;
    color: #94a3b8;
    transition: transform 0.3s;
    
    &.expanded {
      transform: rotate(180deg);
    }
  }
}

.score-details-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.score-item {
  display: grid;
  grid-template-columns: 100px 1fr 40px;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f1f5f9;
  }
  
  .score-label {
    color: #475569;
    font-weight: 500;
  }
  
  .score-value {
    text-align: right;
    color: #1e293b;
    font-weight: 600;
  }
}

.audio-player-section {
  margin-bottom: 16px;
  
  h6 {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    
    i {
      margin-right: 6px;
      color: #3b82f6;
    }
  }
  
  .audio-control {
    width: 100%;
    height: 40px;
    border-radius: 4px;
    background: white;
    border: 1px solid #e2e8f0;
    outline: none;
    
    &::-webkit-media-controls-panel {
      background-color: white;
    }
  }
  
  .audio-info {
    margin-top: 8px;
    
    .audio-hint {
      font-size: 12px;
      color: #64748b;
      display: flex;
      align-items: center;
      gap: 4px;
      
      i {
        font-size: 14px;
        color: #3b82f6;
      }
    }
  }
}

.loading-placeholder,
.empty-placeholder {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  
  i {
    font-size: 32px;
    margin-bottom: 12px;
    display: block;
  }
  
  p {
    font-size: 13px;
    margin: 0;
  }
}

.loading-placeholder {
  i {
    animation: rotate 1s linear infinite;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.panel-overlay {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  max-height: 200px;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

.detail-panel::-webkit-scrollbar {
  width: 6px;
}

.detail-panel::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.detail-panel::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>

<style lang="scss">
.score-description-tooltip {
  max-width: 400px;
  font-size: 12px;
  line-height: 1.6;
  padding: 12px;
  background-color: #ffffff !important;
  color: #1e293b !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-radius: 6px !important;
  
  .el-tooltip__popper {
    max-width: 400px;
    background-color: #ffffff !important;
    color: #1e293b !important;
    border: 1px solid #e2e8f0 !important;
  }
  
  .el-tooltip__arrow {
    border-right-color: #e2e8f0 !important;
    
    &::before {
      border-right-color: #ffffff !important;
    }
  }
  
  &,
  * {
    color: #1e293b !important;
  }
}
</style>
